/**
 * @fileoverview Module for `src/admin/Settings.js`.
 */

import React, { Fragment } from 'react';
import {
  Create,
  Datagrid,
  DateField,
  Edit,
  List,
  Pagination,
  ReferenceManyField,
  SaveButton,
  SelectInput,
  SimpleForm,
  Show,
  ShowButton,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  required,
} from 'react-admin';

/**
 * Toolbar for setting edit forms without delete actions.
 *
 * @param {Object} props
 * @returns {JSX.Element} Edit toolbar.
 */
const SettingEditToolbar = (props) => (
  //removes default delete option from toolbar
  <Toolbar { ...props } >
    <SaveButton />
  </Toolbar>
);

/**
 * Validates setting values: required, an integer for the limits, and 0 or 1
 * for TURNSTILE_ENFORCE.
 *
 * @param {Object} values
 * @returns {Object} Validation errors keyed by field.
 */
const validateSettingValue = (values) => {
  const errors = {};
  if (!values.value) {
    errors.value = 'The value is required';
  } else if (['LETTERKIT_LIMIT', 'STAMPKIT_LIMIT', 'KIT_REQUEST_LIMIT', 'FLYER_REQUEST_LIMIT'].includes(values.type) && !`${ values.value }`.match(/^\d+$/)) {
    // You can return translation keys
    errors.value = 'The value must be an integer.';
  } else if (values.type === 'TURNSTILE_ENFORCE' && !`${ values.value }`.match(/^[01]$/)) {
    errors.value = 'The value must be 0 (log only) or 1 (enforce).';
  }
  return errors;
};

/**
 * Shared setting inputs used by create and edit forms.
 *
 * @returns {JSX.Element} Shared form controls.
 */
const SharedInputs = () => (
  <Fragment>
    <SelectInput
      choices={ [
        { id: 'LETTERKIT_LIMIT', name: 'Letter Kit Limit' },
        { id: 'STAMPKIT_LIMIT', name: 'Stamp Kit Limit' },
        { id: 'KIT_REQUEST_LIMIT', name: 'Kit Request Limit' },
        { id: 'TURNSTILE_ENFORCE', name: 'Enforce Turnstile on account emails' },
      ] }
      source='type'
      validate={ [required()] }
    /><br />
    <TextInput source='value' validate={ [required()] } /><br />
  </Fragment>
);

/**
 * Edit view for settings resources.
 *
 * @param {Object} props
 * @returns {JSX.Element} Setting edit form.
 */
export const SettingEdit = (props) => (
  <Edit undoable={ false } { ...props }>
    <SimpleForm redirect='show' toolbar={ <SettingEditToolbar /> } validate={ validateSettingValue }>
      <TextInput disabled source='id' />
      <SharedInputs />
      <TextField source='currentCount' />
    </SimpleForm>
  </Edit>
);

/**
 * Show view for settings resources, including audit history.
 *
 * @param {Object} props
 * @returns {JSX.Element} Setting details and audits.
 */
export const SettingView = (props) => (
  <Show { ...props }>
    <SimpleShowLayout>
      <TextField source='id' />
      <TextField source='type' />
      <TextField source='value' />
      <TextField source='currentCount' />
      <ReferenceManyField label='Audit' pagination={ <Pagination /> } reference='SettingsAudits' sort={{ field: 'createdAt', order: 'DESC' }} target='settingId'>
        <Datagrid>
          <DateField label='Set on' showTime source='createdAt' />
          <TextField label='Set by' source='users.email' />
          <TextField source='newValue' />
        </Datagrid>
      </ReferenceManyField>
    </SimpleShowLayout>
  </Show>
);

/**
 * Toolbar used by setting create forms.
 *
 * @param {Object} props
 * @returns {JSX.Element} Create toolbar.
 */
const SettingCreateToolbar = (props) => (
  <Toolbar { ...props } >
    <SaveButton
      label='create'
    />
  </Toolbar>
);

/**
 * Create view for settings resources.
 *
 * @param {Object} props
 * @returns {JSX.Element} Setting create form.
 */
export const SettingCreate = (props) => (
  <Create { ...props }>
    <SimpleForm redirect='show' toolbar={ <SettingCreateToolbar /> } validate={ validateSettingValue }>
      <SharedInputs />
    </SimpleForm>
  </Create>
);


/**
 * List view for settings resources.
 *
 * @param {Object} props
 * @returns {JSX.Element} Settings list.
 */
const SettingList = (props) => (
  <List { ...props }>
    <Datagrid>
      <TextField source='id' />
      <TextField source='type' />
      <TextField source='value' />
      <TextField source='currentCount' />
      <TextField source='createdAt' />
      <ShowButton { ...props } />
    </Datagrid>
  </List>
);

export default SettingList;
